import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Pause, Play, ExternalLink } from "lucide-react";
import { API } from "@/lib/faro";

const STORY_DURATION_MS = 2500; // 2.5s per story (reel-like pace)

// Responsive AVIF/WebP variants generated at /assets/hero/{slug}-{width}.{avif|webp}
// Falls back to original when the incoming path has no known variant.
const HERO_VARIANTS = {
  "/assets/cuadriciclo-rojo-practicas-aml-vigo.webp": "cuadriciclo-aml",
  "/assets/moto-kawasaki-z400-carnet-a2-vigo.webp": "moto-a2",
  "/assets/camion-carnet-c-vigo.webp": "camion-c",
  "/assets/furgoneta-remolque-carnet-be-vigo.webp": "furgoneta-be",
  "/assets/autobus-carnet-d-vigo.webp": "autobus-d",
  "/assets/permiso-por-puntos-conductor-vigo.webp": "puntos",
};

const heroSrcSet = (slug, ext) =>
  `/assets/hero/${slug}-480.${ext} 480w, /assets/hero/${slug}-800.${ext} 800w, /assets/hero/${slug}-1200.${ext} 1200w`;
const HERO_SIZES = "(min-width: 640px) 400px, 100vw";

// Seed with the first post that the backend serves. Ensures the LCP image is present
// in the prerendered/SSR HTML and can be preloaded from index.html, avoiding a wait on the API.
const SEED_POSTS = [
  {
    id: "ig1",
    image: "/assets/cuadriciclo-rojo-practicas-aml-vigo.webp",
    caption: "Únicos en el sur de Galicia con cuadriciclo propio para el AML 🚗✨",
    posted_ago: "hace 2 días",
    permalink: "https://www.instagram.com/autoescuelasfaro/",
  },
];

const StoryProgressBars = ({ bars, idx, progress }) => (
  <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
    {bars.map((b) => (
      <div key={b} className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden">
        <div
          className="h-full bg-white transition-none"
          style={{
            width: b < idx ? "100%" : b === idx ? `${progress * 100}%` : "0%",
          }}
        />
      </div>
    ))}
  </div>
);

const StoryHeader = ({ postedAgo, paused, onTogglePause }) => (
  <div className="absolute top-7 left-4 right-4 flex items-center gap-3 z-10 mt-2">
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center"
      style={{
        background:
          "conic-gradient(from 45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888, #f09433)",
        padding: 2,
      }}
    >
      <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
        <Instagram size={18} className="text-[var(--faro-blue)]" />
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-white text-sm font-bold leading-tight truncate">@autoescuelasfaro</div>
      <div className="text-white/80 text-[11px] leading-tight">{postedAgo}</div>
    </div>
    <button
      type="button"
      onClick={onTogglePause}
      aria-label={paused ? "Reproducir" : "Pausar"}
      data-testid="ig-story-pause"
      className="w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur flex items-center justify-center text-white transition-colors"
    >
      {paused ? <Play size={14} fill="currentColor" /> : <Pause size={14} fill="currentColor" />}
    </button>
  </div>
);

const StoryFooter = ({ caption, permalink }) => (
  <div className="absolute bottom-4 left-4 right-4 z-10">
    <p className="text-white text-sm leading-snug mb-3 line-clamp-3 drop-shadow-lg">
      {caption}
    </p>
    <a
      href={permalink}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="ig-story-cta"
      className="w-full inline-flex items-center justify-center gap-2 bg-white/95 hover:bg-white text-[var(--faro-blue-deep)] font-black uppercase tracking-wider text-xs py-3 rounded-full transition-colors"
    >
      Síguenos en Instagram <ExternalLink size={13} />
    </a>
  </div>
);

function useStoryProgress(total, paused, idx, setIdx) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const elapsedRef = useRef(0);

  const reset = () => {
    elapsedRef.current = 0;
    setProgress(0);
    startRef.current = performance.now();
  };

  useEffect(() => {
    if (!total || paused) return;
    startRef.current = performance.now() - elapsedRef.current;
    const tick = (t) => {
      const el = t - startRef.current;
      elapsedRef.current = el;
      const p = Math.min(el / STORY_DURATION_MS, 1);
      setProgress(p);
      if (p >= 1) {
        setIdx((i) => (i + 1) % total);
        elapsedRef.current = 0;
        setProgress(0);
        startRef.current = performance.now();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [total, paused, idx, setIdx]);

  return { progress, reset };
}

const StoryTapZones = ({ onPrev, onNext }) => (
  <>
    <button
      type="button"
      aria-label="Anterior"
      data-testid="ig-story-prev"
      onClick={onPrev}
      className="absolute inset-y-0 left-0 w-1/3 z-[5] focus:outline-none"
    />
    <button
      type="button"
      aria-label="Siguiente"
      data-testid="ig-story-next"
      onClick={onNext}
      className="absolute inset-y-0 right-0 w-1/3 z-[5] focus:outline-none"
    />
  </>
);

const StoryImage = ({ current, idx, isLcpCandidate }) => {
  const slug = HERO_VARIANTS[current.image];
  const isFirst = idx === 0;
  // The very first painted slide (idx=0 and LCP candidate) skips the opacity fade so
  // Chrome can mark LCP as soon as the bytes arrive. Later slides keep the crossfade.
  const initial = isFirst && isLcpCandidate ? { opacity: 1, scale: 1.02 } : { opacity: 0, scale: 1.02 };

  const imgAttrs = {
    src: current.image,
    alt: current.caption || "Publicación de Instagram de Autoescuelas Faro",
    width: 800,
    height: 1000,
    fetchpriority: isFirst ? "high" : "auto",
    decoding: "async",
    loading: isFirst ? "eager" : "lazy",
    className: "absolute inset-0 w-full h-full object-cover origin-center will-change-transform",
    "data-testid": `ig-story-image-${idx}`,
  };

  return (
    <AnimatePresence mode="wait">
      <motion.picture
        key={current.id}
        initial={initial}
        animate={{ opacity: 1, scale: 1.14 }}
        exit={{ opacity: 0 }}
        transition={{
          opacity: { duration: isFirst && isLcpCandidate ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] },
          scale: { duration: STORY_DURATION_MS / 1000, ease: "linear" },
        }}
        className="absolute inset-0 w-full h-full origin-center will-change-transform block"
      >
        {slug && <source type="image/avif" srcSet={heroSrcSet(slug, "avif")} sizes={HERO_SIZES} />}
        {slug && <source type="image/webp" srcSet={heroSrcSet(slug, "webp")} sizes={HERO_SIZES} />}
        <img {...imgAttrs} />
      </motion.picture>
    </AnimatePresence>
  );
};

export const InstagramStories = () => {
  const [posts, setPosts] = useState(SEED_POSTS);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // The very first slide painted (before the API responds) is the LCP candidate.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/instagram/feed`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setPosts(r.data);
      })
      .catch(() => {})
      .finally(() => setHydrated(true));
  }, []);

  const total = posts.length;
  const current = posts[idx];
  const { progress, reset } = useStoryProgress(total, paused, idx, setIdx);

  const goTo = (delta) => {
    setIdx((i) => (i + delta + Math.max(total, 1)) % Math.max(total, 1));
    reset();
  };

  const bars = useMemo(() => Array.from({ length: total }, (_, i) => i), [total]);

  if (!total) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--faro-bg)] text-[var(--faro-text-soft)] text-sm">
        Cargando historias…
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-full select-none"
      data-testid="instagram-stories"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <StoryImage current={current} idx={idx} isLcpCandidate={!hydrated} />

      {/* Top gradient */}
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 via-black/25 to-transparent pointer-events-none" />
      {/* Bottom gradient */}
      <div className="absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />

      <StoryProgressBars bars={bars} idx={idx} progress={progress} />
      <StoryHeader postedAgo={current.posted_ago} paused={paused} onTogglePause={() => setPaused((p) => !p)} />
      <StoryTapZones onPrev={() => goTo(-1)} onNext={() => goTo(1)} />
      <StoryFooter caption={current.caption} permalink={current.permalink} />
    </div>
  );
};

export default InstagramStories;
